<script setup lang="ts">
import { computed, defineProps } from "vue";
import { Author, AuthorTranslation, RelatedBlog } from "@/types/blogs";
import { useLangStore } from "@/stores/langStore";

import facebook from "@/assets/icons/social/facebook.svg";
import messenger from "@/assets/icons/social/messenger.svg";
import telegram from "@/assets/icons/social/telegram.svg";
import viber from "@/assets/icons/social/viber.svg";
import x from "@/assets/icons/social/x.svg";
import { useRouter } from "vue-router";
import { ROUTE_NAMES } from "@/constants/routeNames";
import { useI18n } from "vue-i18n";

const props = defineProps<{
  author: Author;
  relatedBlogs: RelatedBlog[];
}>();

const socials = [
  { src: facebook, alt: "Facebook" },
  { src: messenger, alt: "Messenger" },
  { src: telegram, alt: "Telegram" },
  { src: viber, alt: "Viber" },
  { src: x, alt: "X" },
];

const languageStore = useLangStore();

const { t } = useI18n();
const router = useRouter();

const currentAuthor = computed<AuthorTranslation>(
  () =>
    props.author[languageStore.current] ||
    props.author[languageStore.fallbackLang]
);

function openRelatedBlog(relatedBlog: RelatedBlog) {
  router.push({
    name: ROUTE_NAMES.BLOG_DETAIL,
    params: { id: relatedBlog.id },
  });
}
</script>

<template>
  <div
    class="w-full h-full flex flex-col xl:flex-col md:flex-wrap md:flex-row bg-brown text-left"
  >
    <!-- AUTHOR INFO -->
    <div
      class="pl-4 md:pl-6 xl:pl-8 border-b border-white border-opacity-20 w-full md:w-1/2 xl:w-full"
    >
      <p
        class="sf-pro font-semibold text-base leading-[21px] xl:text-xl xl:leading-[28px] text-muted mt-6 xl:mt-8"
      >
        {{ t("blog_detail.article_author") }}
      </p>
      <div class="mt-4 xl:mt-5 mb-6 xl:mb-8 flex">
        <img
          :src="props.author?.photo_url"
          alt="Author Photo"
          class="w-[72px] h-[72px] rounded-full"
        />
        <div class="flex flex-col ml-4 my-[7.5px]">
          <p
            class="flex-1 text-white sf-pro font-medium text-2xl leading-100p xl:font-semibold xl:leading-[32px]"
          >
            {{ currentAuthor?.name ?? "" }}
          </p>
          <p
            class="flex-1 text-ternary sf-pro font-normal text-sm leading-[21px] xl:text-[13px]"
          >
            {{ currentAuthor?.position ?? "" }}
          </p>
        </div>
      </div>
    </div>
    <!-- SHARED LINKS -->
    <div
      v-if="false"
      class="pl-4 md:pl-0 xl:pl-8 border-b border-white border-opacity-20 w-full md:w-1/2 xl:w-full"
    >
      <p
        class="text-muted mt-6 xl:mt-8 sf-pro font-semibold text-base leading-[21px] xl:text-xl xl:leading-[28px]"
      >
        {{ t("blog_detail.share_with_friends") }}
      </p>
      <div class="flex gap-2 mt-4 xl:mt-5 mb-6 md:mb-[41px] xl:mb-8">
        <img
          v-for="s in socials"
          :key="s.name"
          class="cursor-pointer"
          :src="s.src"
          :alt="s.alt"
        />
      </div>
    </div>
    <!-- RELATED ARTICLES -->
    <div
      class="px-4 md:px-6 xl:px-8 border-b border-white border-opacity-20 w-full"
      v-if="props.relatedBlogs.length > 0"
    >
      <p
        class="text-muted sf-pro font-semibold text-base leading-[21px] xl:text-xl xl:leading-[28px] mt-6 xl:mt-8"
      >
        {{ t("blog_detail.similar_articles") }}
      </p>
      <div
        class="mt-5 mb-6 xl:mb-8 flex flex-row flex-wrap xl:flex-col gap-y-[16px] xl:gap-y-[24px] max-h-[368px] md:max-h-[176px] xl:max-h-[395px] overflow-y-auto no-scrollbar"
      >
        <div
          v-for="relatedBlog in props.relatedBlogs"
          :key="relatedBlog.id"
          class="flex cursor-pointer w-full md:w-1/2 xl:w-full"
          @click="openRelatedBlog(relatedBlog)"
        >
          <img
            :src="relatedBlog.image_url"
            alt="Related Blog Img"
            class="w-[110px] h-20 rounded-[10px] object-cover"
          />
          <div class="flex flex-col ml-4">
            <p class="text-muted sf-pro-xs mb-1">
              {{ relatedBlog.created_at }}
            </p>
            <p
              class="flex-1 text-ternary sf-pro font-semibold text-base leading-[21px] xl:font-medium xl:leading-[24px]"
            >
              {{
                relatedBlog.titles[languageStore.current] ||
                relatedBlog.titles[languageStore.fallbackLang]
              }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
